import React, { useState, useEffect } from 'react';
import { X, Download, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { 
  Button, 
  Input, 
  Box, 
  Modal, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Link
} from '@mui/material';
import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';

// Styled components (keep all your existing styled components)
const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ModalContent = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '95vw',
  maxWidth: '1200px',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: theme.shadows[24],
  overflow: 'hidden',
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  background: 'linear-gradient(to right, #6366f1, #9333ea)',
  borderTopLeftRadius: '12px',
  borderTopRightRadius: '12px',
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  flex: 1,
}));

const HeaderActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: '#f8fafc',
}));

const TableWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    padding: '8px 12px',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableCell-head': {
    backgroundColor: '#f1f5f9',
    fontWeight: 600,
    color: '#334155',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .MuiTableCell-body': {
    color: '#475569',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: '#f8fafc',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: '#f8fafc',
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(12),
  color: '#64748b',
}));

const LeadDetailsModal = ({ isOpen, onClose, title, leads, columns }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Reset pagination and search when modal opens
  useEffect(() => {
    if (isOpen) {
      setPage(0);
      setSearchTerm('');
    }
  }, [isOpen]);

  if (!leads) return null;

  // Filter leads by search term
  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return Object.values(lead).some(val => 
      val && val.toString().toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const startIdx = page * rowsPerPage;
  const paginatedLeads = filteredLeads.slice(startIdx, startIdx + rowsPerPage);

  // Default columns to show
  const displayColumns = columns || [
    'Lead No.',
    'Lead Assigned Date',
    'Experience Center',
    'City',
    'Current Stage',
    'Assigned To',
    'Lead Type',
    'Source Of Lead'
  ];

  const handleExport = () => {
    // Transform and prepare data for export with sequential serial numbers
    const exportData = filteredLeads.map((lead, index) => {
      // Create a new object without _id
      const { _id, monthKey, ...rest } = lead;
      
      // Transform the data
      const transformedData = {};
      
      Object.entries(rest).forEach(([key, value]) => {
        // Replace Sl. No. with sequential number (index + 1)
        if (key === 'Sl. No.') {
          transformedData['Sl. No.'] = index + 1; 
        }
        // Rename meetingMonthKey to Month Key
        else if (key === 'Meeting Date') {
          return;
        } else {
          transformedData[key] = value;
        }
      });
      
      return transformedData;
    });
    
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Optional: Auto-size columns
    const maxWidth = 50;
    const wscols = Object.keys(exportData[0] || {}).map(() => ({ wch: 15 }));
    ws['!cols'] = wscols;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    XLSX.writeFile(wb, `leads_export_${Date.now()}.xlsx`);
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <StyledModal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="lead-details-modal"
    >
      <ModalContent elevation={24}>
        {/* Header */}
        <ModalHeader>
          <HeaderContent>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: 'white',
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#c7d2fe',
                fontSize: '0.875rem',
              }}
            >
              {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''} found
            </Typography>
          </HeaderContent>
          <HeaderActions>
            <Button
              onClick={handleExport}
              variant="contained"
              size="small"
              startIcon={<Download size={16} />}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
                textTransform: 'none',
                boxShadow: 'none',
              }}
            >
              Export
            </Button>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <X size={20} />
            </IconButton>
          </HeaderActions>
        </ModalHeader>

        {/* Search */}
        <SearchContainer>
          <TextField
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            data-testid="lead-search"
            size="small"
            sx={{
              maxWidth: '400px',
              width: '100%',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#cbd5e1',
                },
                '&:hover fieldset': {
                  borderColor: '#94a3b8',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6366f1',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} style={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
          />
        </SearchContainer>

        {/* Table */}
        <TableWrapper>
          {paginatedLeads.length > 0 ? (
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: '100%' }}>
              <StyledTable stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    {displayColumns.map(col => (
                      <TableCell
                        key={col}
                        sx={{
                          fontWeight: 600,
                          whiteSpace: col === 'Last Notes' ? 'normal' : 'nowrap',
                          ...(col === 'Last Notes' ? { minWidth: 220 } : {}),
                          ...(col === 'Has Notes' ? { minWidth: 100 } : {}),
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLeads.map((lead, idx) => (
                    <StyledTableRow key={lead._id || idx}>
                      <TableCell>{startIdx + idx + 1}</TableCell>
                      {displayColumns.map(col => {
                        // Check if this is the "Lead No." column to render as link
                        if (col === 'Lead No.' && lead._id) {
                          return (
                            <TableCell key={col}>
                              <Link
                                href={`/project-logs/${lead._id}`}
                                target="_blank"
                                sx={{
                                  color: "#000",
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    color: '#6366f1',
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                {lead[col] ?? lead.lead_no ?? '-'}
                              </Link>
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={col}>
                            {lead[col] !== undefined && lead[col] !== null ? lead[col] : '-'}
                          </TableCell>
                        );
                      })}
                    </StyledTableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </TableContainer>
          ) : (
            <EmptyState>
              <Typography variant="body1" sx={{ color: '#64748b' }}>
                No leads found matching your search.
              </Typography>
            </EmptyState>
          )}
        </TableWrapper>

        {/* Pagination */}
        {filteredLeads.length > 0 && (
          <PaginationContainer>
            <Typography variant="body2" sx={{ color: '#475569' }}>
              Showing {startIdx + 1} - {Math.min(startIdx + rowsPerPage, filteredLeads.length)} of {filteredLeads.length}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
                size="small"
                sx={{
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#f1f5f9',
                  },
                  '&.Mui-disabled': {
                    borderColor: '#e2e8f0',
                  },
                }}
              >
                <ChevronLeft size={16} />
              </IconButton>
              <Typography variant="body2" sx={{ color: '#475569', px: 1 }}>
                Page {page + 1} of {Math.ceil(filteredLeads.length / rowsPerPage)}
              </Typography>
              <IconButton
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(filteredLeads.length / rowsPerPage) - 1}
                size="small"
                sx={{
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#f1f5f9',
                  },
                  '&.Mui-disabled': {
                    borderColor: '#e2e8f0',
                  },
                }}
              >
                <ChevronRight size={16} />
              </IconButton>
            </Box>
          </PaginationContainer>
        )}
      </ModalContent>
    </StyledModal>
  );
};

export default LeadDetailsModal;